import { useState } from "react"
import { Head } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { CsrfField, FieldError } from "@/components/tuw/auth/fields"
import OnboardingLayout, { StepTitle, stepKicker } from "@/components/tuw/onboarding/OnboardingLayout"
import UsageAgreementText from "@/components/tuw/onboarding/UsageAgreementText"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

const BOX_CLASS =
  "size-5 rounded-[6px] border-[1.5px] border-line-2 bg-card data-[state=checked]:border-foreground data-[state=checked]:bg-foreground data-[state=checked]:text-background data-[state=indeterminate]:border-foreground data-[state=indeterminate]:bg-foreground data-[state=indeterminate]:text-background"

// Step two of onboarding (P11-H5 House Rules): the platform's ground rules and
// the seven policies, each its own posted boolean (`customer[<key>]`) so the
// server's all-seven rule is unchanged. An unchecked box posts nothing, which
// the server reads as false. The master box only drives the seven.
export default function Agree({ email, rail, initials, signOut, action, policies, sharedAccess = false, flash = {} }) {
  const [agreed, setAgreed] = useState({})
  const count = policies.filter((policy) => agreed[policy.name]).length
  const all = count === policies.length
  const master = all ? true : count > 0 ? "indeterminate" : false

  function setAll(checked) {
    setAgreed(Object.fromEntries(policies.map((policy) => [policy.name, checked === true])))
  }

  return (
    <OnboardingLayout step={2} rail={rail} email={email} initials={initials} signOut={signOut}>
      <Head title="Usage agreement · The Urban Writers" />

      <StepTitle kicker={stepKicker(2, rail)} title="A few ground rules">
        Welcome to the community at Dibbly | The Urban Writers. Before we take you to your dashboard, here’s what we
        ask of everyone on the platform.
      </StepTitle>

      {flash.notice && (
        <AuthAlert tone="good">
          <b>Email verified.</b>{" "}
          {sharedAccess
            ? "You’ve also been given access to a project someone shared with you. It’ll be waiting on your dashboard."
            : "Welcome!"}
        </AuthAlert>
      )}

      <UsageAgreementText />

      <form method="post" action={action} className="flex flex-col gap-4">
        <CsrfField />
        <div className="flex flex-col gap-[7px]">
          <Label className="text-[12.5px] font-bold">Please read and agree to each policy</Label>
          <div
            className={cn(
              "flex flex-col overflow-hidden rounded-[14px] border bg-card",
              flash.alert ? "border-primary" : "border-border"
            )}
          >
            <div className="flex items-center gap-3 border-b border-border bg-secondary px-4 py-3.5">
              <Checkbox
                id="agree_all"
                checked={master}
                onCheckedChange={setAll}
                aria-label="I have read and agree to all seven policies"
                className={BOX_CLASS}
              />
              <Label htmlFor="agree_all" className="flex flex-col items-start gap-0 text-[13.5px] font-bold">
                I have read and agree to all seven policies
                <small data-testid="agree-count" className="text-[12px] font-medium text-ink-3">
                  {count} of {policies.length} checked
                </small>
              </Label>
            </div>
            {policies.map((policy) => (
              <div key={policy.name} className="flex items-center gap-3 border-b border-border px-4 py-[11px] last:border-b-0">
                <Checkbox
                  id={policy.name}
                  name={policy.name}
                  value="true"
                  checked={agreed[policy.name] === true}
                  onCheckedChange={(checked) => setAgreed({ ...agreed, [policy.name]: checked === true })}
                  className={BOX_CLASS}
                />
                <Label htmlFor={policy.name} className="flex-1 text-[13.5px] font-medium">
                  {policy.label}
                </Label>
                <a
                  href={policy.url}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-[3px] text-[12px] font-semibold text-muted-foreground hover:text-foreground"
                >
                  Read <ExternalLink className="size-3" strokeWidth={2} aria-hidden="true" />
                </a>
              </div>
            ))}
          </div>
          {flash.alert && <FieldError>{flash.alert}</FieldError>}
        </div>

        <div className="flex items-center justify-between gap-3 pt-1.5 max-[560px]:flex-col-reverse max-[560px]:items-stretch">
          <span className="text-[12.5px] text-ink-3">Each policy opens in a new tab.</span>
          <Button
            type="submit"
            disabled={!all}
            className="h-auto rounded-[11px] px-5 py-3 text-[14px] font-semibold"
          >
            Agree and continue
          </Button>
        </div>
      </form>
    </OnboardingLayout>
  )
}
